import React from 'react'
import styled from 'styled-components'
import { graphql, withApollo, compose } from 'react-apollo'
import PropTypes from 'prop-types'
import moment from 'moment'
import LoadingButton from '../common/LoadingButton'
import Button from '../common/Button'
import DestroyFiscalYear from './queries/DestroyFiscalYear'
import DestroyCashflowData from './queries/DestroyCashflowData'
import FadeInUpWrapper from '../common/FadeInUpWrapper'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import CreateFiscalYear from './queries/CreateFiscalYear'
import CacheUtil from '../common/utils/CacheUtil'
import CreateTaxReturn from './queries/CreateTaxReturn'

const Container = styled(FadeInUpWrapper)`
  border: 1px solid #c1c1c1;
  border-radius: 5px;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`
const ButtonContainer = styled.div`
  display: flex;
  width: 40%;
  justify-content: space-between;
  margin-top: 10px;
`
const GreyButton = Button.extend`
  background: #dbdbdc;
  color: #6f6f73;
  padding: 5px 15px;
`
const SkinnyLoadingButton = styled(LoadingButton)`
  padding: 5px 15px;
`

class RemoveInterimModal extends React.Component {
  constructor() {
    super()
    this.state = {
      isLoading: false
    }
  }
  destroyEntityCashflowData = async entity => {
    const { destroyCashflowData, year } = this.props
    const { cashflowData, __typename, rid } = entity
    const dataKeys = cashflowData.filter(d => d.year === year && d.source !== 'derived' && d.source !== 'tax_transcript')
      .map(d => ({ year: d.year, period: d.period, category: d.category, source: d.source }))
    if (dataKeys.length > 0) {
      return destroyCashflowData({
        variables: {
          dataKeys,
          resourceType: __typename,
          resourceId: rid
        }
      })
    }
  }
  updateCachedFiscalYears = async () => {
    const { client, loan, cashflowOptions, loanId, year, queriedYears, createFiscalYear, createTaxReturn, resetYearSources } = this.props
    const years = loan.businesses[0].applicableYears
    const entities = CacheUtil.getEntities(loan)
    // We are removing a year so we get the range of years from current year -1 to current year -6
    const newApplicableYears = Array.from(new Array(5), (_, idx) => year - idx - 1).reverse()
    const queryVariables = { loanId, years: queriedYears, cashflowOptions }
    const data = client.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
    const earliestYear = Math.min(...years) - 1
    const query = await client.query({
      query: FinancialReviewQuery,
      variables: {
        cashflowOptions,
        loanId,
        years: [earliestYear]
      },
      options: {
        fetchPolicy: 'no-cache'
      }
    })
    data.loan.businesses[0].applicableYears = newApplicableYears
    data.loan.cashflowData.push(...query.data.loan.cashflowData)
    for (const e of entities) {
      // We have to find the correct entity in the cache in order mutate it and
      // reuse the entity we pulled out of the cache above instead of reading
      // from the store with each mutation
      const cachedEntity = CacheUtil.findEntityInQuery(e, data)
      const queriedEntity = CacheUtil.findEntityInQuery(e, query.data)
      cachedEntity.fiscalYears.splice(0, 1)
      if (cachedEntity.fiscalYears.length < 5) {
        await createFiscalYear({
          variables: {
            entityType: e.__typename,
            entityId: e.rid,
            year: earliestYear,
            annualizationCoefficient: 1,
            period: 'tax',
            days: moment([earliestYear]).isLeapYear() ? 366 : 365
          },
          update: (_, { data: { createFiscalYear: { fiscalYear } } }) => {
            cachedEntity.fiscalYears.push(fiscalYear)
          }
        })
        if (e.__typename === 'Business') {
          await createTaxReturn({
            variables: {
              businessId: e.rid,
              year: earliestYear,
              formName: '1120'
            },
            update: (_, { data: { createTaxReturn: { taxReturn } } }) => {
              cachedEntity.taxReturns.unshift(taxReturn)
            }
          })
        }
      }
      cachedEntity.cashflowData.push(...queriedEntity.cashflowData) // Add cashflow data from the prior year to the cache
    }
    client.writeQuery({ query: FinancialReviewQuery, variables: { cashflowOptions, loanId, years: queriedYears }, data })
    resetYearSources()
  }
  _createTaxReturn = (entity, year) => {
    const { createTaxReturn, cashflowOptions, loanId, queriedYears } = this.props
    const queryVariables = { cashflowOptions, loanId, years: queriedYears }
    if (entity.__typename === 'Business') {
      return createTaxReturn({
        variables: {
          businessId: entity.rid,
          year,
          formName: '1120'
        },
        update: (store, { data: { createTaxReturn } }) => {
          const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
          const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
          cachedEntity.taxReturns.unshift(createTaxReturn)
          store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
        }
      })
    }
  }
  updateCachedCashflowData = () => {
    const { client, loan, cashflowOptions, year, loanId, queriedYears } = this.props
    const data = client.readQuery({ query: FinancialReviewQuery, variables: { cashflowOptions, loanId, years: queriedYears } })
    const entities = CacheUtil.getEntities(loan)
    for (const e of entities) {
      const cachedEntity = CacheUtil.findEntityInQuery(e, data)
      const { cashflowData } = cachedEntity
      cachedEntity.cashflowData = cashflowData.filter(d => d.year !== year)
    }
    client.writeQuery({ query: FinancialReviewQuery, variables: { cashflowOptions, loanId, years: queriedYears }, data })
  }
  onRemove = async () => {
    const { destroyFiscalYear, loan, onCancel, year } = this.props
    const entities = CacheUtil.getEntities(loan)
    this.setState({ isLoading: true })
    const fiscalYearMutations = entities.map(e => {
      const { rid } = e.fiscalYears.find(fy => fy.year === year)
      return destroyFiscalYear({
        variables: {
          rid,
          entityType: e.__typename
        }
      })
    })
    const destroyCashflowDataMutations = entities.map(e => this.destroyEntityCashflowData(e))
    try {
      await Promise.all(fiscalYearMutations)
      await Promise.all(destroyCashflowDataMutations)
      this.updateCachedCashflowData()
      await this.updateCachedFiscalYears()
    } catch (err) {
      console.log(err)
    } finally {
      onCancel()
    }
  }
  render() {
    const { year, onCancel } = this.props
    const { isLoading } = this.state
    return (
      <Container>
        <h3>Are you sure you want to remove {year} interim period?</h3>
        <ButtonContainer>
          <GreyButton onClick={onCancel}>
            Cancel
          </GreyButton>
          <SkinnyLoadingButton loading={isLoading} onClick={this.onRemove}>
            Remove
          </SkinnyLoadingButton>
        </ButtonContainer>
      </Container>
    )
  }
}

RemoveInterimModal.propTypes = {
  cashflowOptions: PropTypes.object.isRequired,
  client: PropTypes.shape({
    readQuery: PropTypes.func.isRequired,
    query: PropTypes.func.isRequired,
    writeQuery: PropTypes.func.isRequired
  }),
  createTaxReturn: PropTypes.func.isRequired,
  destroyCashflowData: PropTypes.func.isRequired,
  destroyFiscalYear: PropTypes.func.isRequired,
  loan: PropTypes.object.isRequired,
  loanId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  queriedYears: PropTypes.arrayOf(PropTypes.number).isRequired,
  year: PropTypes.number.isRequired
}

export default compose(
  graphql(DestroyFiscalYear, {
    name: 'destroyFiscalYear'
  }),
  graphql(DestroyCashflowData, {
    name: 'destroyCashflowData'
  }),
  graphql(CreateFiscalYear, {
    name: 'createFiscalYear'
  }),
  graphql(CreateTaxReturn, {
    name: 'createTaxReturn'
  }),
  withApollo
)(RemoveInterimModal)
